import React from 'react';
import { Helmet } from 'react-helmet-async';

const Compliance = () => {
  return (
    <div style={{paddingTop: '100px'}}>
      <Helmet>
        <title>Compliance & Regulatory Information - TCPA, CTIA, A2P 10DLC | RMQ Digital</title>
        <meta name="description" content="RMQ Digital compliance: TCPA regulations, CTIA guidelines, A2P 10DLC registration, and data protection. 100% compliant customer communication services." />
        <meta name="keywords" content="TCPA compliance, CTIA guidelines, A2P 10DLC, regulatory compliance, messaging compliance, data protection" />
      </Helmet>
      
      <section style={{padding: '4rem 2rem', background: '#f9fafb'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto'}}>
          <h1 style={{fontSize: '3rem', fontWeight: '800', marginBottom: '1rem', color: '#1f2937', fontFamily: 'Space Grotesk'}}>Compliance & Regulatory Information</h1>
          
          {/* Compliance Hero Image */}
          <div style={{ marginBottom: '4rem', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
            <img 
              src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&h=400&fit=crop" 
              alt="Compliance and security" 
              style={{ width: '100%', height: '400px', objectFit: 'cover' }}
            />
          </div>

          <div style={{maxWidth: '800px', margin: '0 auto', lineHeight: '1.8'}}>
            <div style={{background: '#ede9fe', padding: '2rem', borderRadius: '12px', marginBottom: '2rem', border: '2px solid #8b5cf6'}}>
              <h2 style={{color: '#6d28d9', marginBottom: '1rem', fontSize: '1.5rem'}}>Our Compliance Commitment</h2>
              <p style={{color: '#5b21b6'}}>
                RMQ Digital is committed to maintaining the highest standards of compliance with all applicable 
                federal and state regulations governing customer communications, messaging services, and consumer protection.
              </p>
            </div>

            <h2 style={{color: '#6d28d9', marginBottom: '1rem', fontSize: '1.75rem'}}>TCPA Compliance</h2>
            <p style={{marginBottom: '1rem'}}>
              We strictly adhere to the Telephone Consumer Protection Act (TCPA) requirements:
            </p>
            <ul style={{marginBottom: '2rem', paddingLeft: '2rem'}}>
              <li>Prior express written consent required for all commercial messages</li>
              <li>Clear and conspicuous disclosure of message frequency and charges</li>
              <li>Easy opt-out mechanisms (STOP keyword)</li>
              <li>Proper record-keeping of all consent and opt-out requests</li>
              <li>Compliance with messaging time restrictions</li>
              <li>Regular consent verification and validation</li>
            </ul>

            <h2 style={{color: '#6d28d9', marginBottom: '1rem', fontSize: '1.75rem'}}>CTIA Guidelines</h2>
            <p style={{marginBottom: '1rem'}}>
              All SMS campaigns follow CTIA (Cellular Telecommunications Industry Association) guidelines:
            </p>
            <ul style={{marginBottom: '2rem', paddingLeft: '2rem'}}>
              <li>Clear opt-in and opt-out processes</li>
              <li>Transparent message frequency disclosure</li>
              <li>Proper identification of message senders</li>
              <li>SMS consent not transferable to third parties</li>
              <li>Compliance with carrier-specific requirements</li>
            </ul>

            <h2 style={{color: '#6d28d9', marginBottom: '1rem', fontSize: '1.75rem'}}>A2P 10DLC Registration</h2>
            <p style={{marginBottom: '1rem'}}>
              RMQ Digital maintains A2P (Application-to-Person) 10DLC compliance:
            </p>
            <ul style={{marginBottom: '2rem', paddingLeft: '2rem'}}>
              <li>Registered with The Campaign Registry (TCR)</li>
              <li>Business verification documentation on file</li>
              <li>Campaign registration for all messaging use cases</li>
              <li>Following carrier-specific requirements</li>
              <li>Monitoring carrier policy updates</li>
              <li>Proper message classification and content guidelines</li>
            </ul>

            <h2 style={{color: '#6d28d9', marginBottom: '1rem', fontSize: '1.75rem'}}>CAN-SPAM Act Compliance</h2>
            <p style={{marginBottom: '1rem'}}>
              All email campaigns comply with the CAN-SPAM Act:
            </p>
            <ul style={{marginBottom: '2rem', paddingLeft: '2rem'}}>
              <li>Accurate "From" and "Subject" lines</li>
              <li>Clear identification as advertisement</li>
              <li>Valid physical postal address in all emails</li>
              <li>Easy-to-use unsubscribe mechanism</li>
              <li>Honor opt-out requests within 10 business days</li>
            </ul>

            <h2 style={{color: '#6d28d9', marginBottom: '1rem', fontSize: '1.75rem'}}>Data Protection & Privacy</h2>
            <p style={{marginBottom: '1rem'}}>
              We implement comprehensive data protection measures:
            </p>
            <ul style={{marginBottom: '2rem', paddingLeft: '2rem'}}>
              <li>Encryption of all customer data in transit and at rest</li>
              <li>Secure data storage and access controls</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Data retention and deletion policies</li>
              <li>Employee training on data protection practices</li>
              <li>Compliance with state privacy laws (CCPA, CPRA, etc.)</li>
              <li>GDPR compliance for international customers</li>
              <li>Secure API access with authentication and rate limiting</li>
            </ul>

            <h2 style={{color: '#6d28d9', marginBottom: '1rem', fontSize: '1.75rem'}}>Carrier Requirements</h2>
            <p style={{marginBottom: '1rem'}}>
              We maintain compliance with all major carrier requirements:
            </p>
            <ul style={{marginBottom: '2rem', paddingLeft: '2rem'}}>
              <li><strong>AT&T:</strong> Registered brand and campaigns, following content guidelines</li>
              <li><strong>T-Mobile:</strong> Compliant with T-Mobile Code of Conduct</li>
              <li><strong>Verizon:</strong> Adhering to Verizon messaging policies</li>
              <li><strong>Message throughput:</strong> Respecting carrier rate limits</li>
              <li><strong>Content filtering:</strong> Avoiding prohibited content categories</li>
              <li><strong>Spam monitoring:</strong> Proactive spam complaint management</li>
            </ul>

            <h2 style={{color: '#6d28d9', marginBottom: '1rem', fontSize: '1.75rem'}}>Consent Management</h2>
            <p style={{marginBottom: '1rem'}}>
              Our consent management system ensures:
            </p>
            <ul style={{marginBottom: '2rem', paddingLeft: '2rem'}}>
              <li>Written consent collected before any commercial messages</li>
              <li>Clear disclosure of message types and frequency</li>
              <li>Timestamp and IP address logging for all opt-ins</li>
              <li>Consent records retained for regulatory compliance</li>
              <li>Separate consent for SMS and email communications</li>
              <li>Ability to revoke consent at any time</li>
              <li>Consent not shared with third parties without permission</li>
            </ul>

            <h2 style={{color: '#6d28d9', marginBottom: '1rem', fontSize: '1.75rem'}}>Prohibited Content</h2>
            <p style={{marginBottom: '1rem'}}>
              We strictly prohibit the following content types:
            </p>
            <ul style={{marginBottom: '2rem', paddingLeft: '2rem'}}>
              <li>Illegal activities or substances</li>
              <li>Phishing or fraudulent schemes</li>
              <li>Adult content or services</li>
              <li>Deceptive or misleading information</li>
              <li>Hate speech or discriminatory content</li>
              <li>High-risk financial services (payday loans, debt relief)</li>
              <li>Cannabis-related products (where prohibited)</li>
              <li>Weapons, ammunition, or explosives</li>
            </ul>

            <h2 style={{color: '#6d28d9', marginBottom: '1rem', fontSize: '1.75rem'}}>Industry Certifications</h2>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem'}}>
              <div style={{background: '#fff', padding: '1.5rem', borderRadius: '12px', border: '2px solid #e5e7eb', textAlign: 'center'}}>
                <h4 style={{color: '#6d28d9', marginBottom: '0.5rem'}}>TCPA Compliant</h4>
                <p style={{fontSize: '0.9rem', color: '#6b7280'}}>Following all TCPA regulations</p>
              </div>
              <div style={{background: '#fff', padding: '1.5rem', borderRadius: '12px', border: '2px solid #e5e7eb', textAlign: 'center'}}>
                <h4 style={{color: '#6d28d9', marginBottom: '0.5rem'}}>A2P Registered</h4>
                <p style={{fontSize: '0.9rem', color: '#6b7280'}}>Carrier-approved messaging</p>
              </div>
              <div style={{background: '#fff', padding: '1.5rem', borderRadius: '12px', border: '2px solid #e5e7eb', textAlign: 'center'}}>
                <h4 style={{color: '#6d28d9', marginBottom: '0.5rem'}}>CTIA Guidelines</h4>
                <p style={{fontSize: '0.9rem', color: '#6b7280'}}>Industry best practices</p>
              </div>
            </div>

            <h2 style={{color: '#6d28d9', marginBottom: '1rem', fontSize: '1.75rem'}}>Contact Information</h2>
            <div style={{background: '#f9fafb', padding: '2rem', borderRadius: '12px', border: '1px solid #e5e7eb'}}>
              <p style={{fontWeight: '600', marginBottom: '1rem'}}>RMQ HOLDINGS INC</p>
              <p><strong>DBA:</strong> RMQ Digital</p>
              <p>2022 EAST 9TH STREET<br />BROOKLYN, NY 11223<br />United States</p>
              <p><strong>Owner:</strong> MOSHE QUBRUSI</p>
              <p><strong>EIN:</strong> 99-1828959</p>
              <p style={{marginTop: '1rem'}}><strong>Email:</strong> support@rmqdigital.com</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Compliance;
